import React from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { SparkApp } from 'spark-core-dx';
import { useCoreContext } from 'spark-core-dx/contexts';
import { Loading, StandardLayout } from 'spark-core-dx/components';
import { DomainRoute } from 'spark-core-dx/domains';
import Home from './domains/public/screens/Home';
import DashboardDomain from './domains/dashboard/DashboardDomain';
import Dashboard from './domains/dashboard/screens/Dashboard';
import DashboardSelect from './domains/dashboard/screens/DashboardSelect';
import DataDomain from './domains/data/DataDomain';
import DataModelWorkspaceSelect from './domains/data/screens/DataModelWorkspaceSelect';
import DataModelDesigner from './domains/data/screens/DataModelDesigner';
import ReportsDomain from './domains/reports/ReportsDomain';
import Reports from './domains/reports/screens/Reports';
import { useCorePermissions, useCoreRoute } from 'spark-core-dx/hooks';
import PublicDomain from './domains/public/PublicDomain'
import SearchSourceObject from './domains/convoke/screens/SearchSourceObject';
import SearchEntityObject from './domains/convoke/screens/SearchEntityObject';
import SearchWarehouse from './domains/convoke/screens/SearchWarehouse';
import SearchWarehouseView from './domains/convoke/screens/SearchWarehouseView';
import ConvokeDomain from './domains/convoke/ConvokeDomain';
import EntityObjectFieldMapping from './domains/convoke/screens/EntityObjectFieldMapping';
import ReportCard from './domains/reports/screens/ReportCard';
import XChatDomain from './domains/multiverXChat/xChatDomain';
import XChat from './domains/multiverXChat/screens/xchat'
import EngineConsoleScreen from './domains/dashboard/screens/EngineConsoleScreen';
import PermissionSetManage from './domains/convoke/screens/PermissionSetManage';
import SourceColumnSetManage from './domains/convoke/screens/SourceColumnSetManage';
import EntityObjectModify from './domains/convoke/screens/EntityObjectModify';
import EntityFieldMapDesignerManage from './domains/convoke/screens/EntityFieldMapDesignerManage';
import MigrationProjectManage from './domains/convoke/screens/MigrationProjectManage';
import MigrationMappingDraftManage from './domains/convoke/screens/MigrationMappingDraftManage';
import EDIDiagnosticsManage from './domains/convoke/screens/EDIDiagnosticsManage';
import EdiTradingPartnerConnectivityManage from './domains/convoke/screens/EdiTradingPartnerConnectivityManage';
import EdiTradingPartnerGroupsManage from './domains/convoke/screens/EdiTradingPartnerGroupsManage';
import Status from './domains/status/screens/Status';
import ExecutionControlDomain from './domains/executionControl/ExecutionControlDomain';
import ExecutionControlCenter from './domains/executionControl/screens/ExecutionControlCenter';
import BillingManagement from './domains/convoke/screens/BillingManagement';
import ResourceApprovalsManage from './domains/convoke/screens/ResourceApprovalsManage';
import AIPanelCommandHost from './components/AIChat/AIPanelCommandHost';
import HardLogout from './domains/auth/HardLogout';
import MarketplaceRoute from './domains/marketplace/MarketplaceRoute';
import MarketplaceCheckoutPanelHost from './domains/marketplace/components/MarketplaceCheckoutPanelHost';
import ResourceDomain from './domains/resource/ResourceDomain';
import ResourceConfigurationRoute from './domains/resource/ResourceConfigurationRoute';
import ResourceWorkspaceRoute from './domains/resource/ResourceWorkspaceRoute';
import { RESOURCES_BASE_PATH, RESOURCES_PARTNER_GROUPS_PATH, RESOURCES_SCHEDULES_PATH, RESOURCES_TRADING_PARTNERS_PATH, RESOURCE_GROUP_BASE_PATH, RESOURCE_WORKSPACE_BASE_PATH } from './domains/resource/resourceRoutes';
import { buildConvokeEntityObjectRoutes, normalizeRoutePath } from './appRoutes';
import McpOAuthConnect from './domains/mcp/McpOAuthConnect';
import {
  BATCH_MANAGEMENT_LEGACY_ROUTE,
  BATCH_MANAGEMENT_LEGACY_SCREEN_KEY,
  EXECUTION_CONTROL_DOMAIN_KEY,
  EXECUTION_CONTROL_OVERVIEW_ROUTE,
  EXECUTION_CONTROL_OVERVIEW_SCREEN_KEY,
  EXECUTION_CONTROL_POOL_PROFILES_ROUTE,
  EXECUTION_CONTROL_POOL_STATUS_CHECKS_LEGACY_ROUTE,
  EXECUTION_CONTROL_ROOT_ROUTE,
  getExecutionControlScreens,
} from './domains/convoke/screens/executionControlCenterConfig';
import CommandShell, {
  CommandAccessDenied,
  CommandRegistryError,
} from './domains/command/CommandShell';
import {
  CLIENT_COMMAND_ACCESS_MODES,
  canAccessClientCommand,
  isClientCommandAccessPending,
  resolveClientCommandAccess,
} from './domains/command/commandAccess';
import {
  getClientCommandRouteScope,
  resolveClientCommandRegistry,
} from './domains/command/clientCommandScreenRegistry';
import {
  CLIENT_COMMAND_NAVIGATION_TARGET_KEY,
  createSelectedClientCommandNavigationItems,
} from './domains/command/commandNavigation';
import { HelpSupportProvider } from './components/helpSupport';

const renderGlobalLoadingScreen = () => (
  <Loading type="spinner-2" size="4rem" />
);

const SystemStatusScreen = () => (
  <StandardLayout>
    <Status />
  </StandardLayout>
);

const App = () => {

  const location = useLocation();
  const isPublicMcpConnectRoute = /^\/mcp\/[^/]+\/(?:oauth\/sign-in|V1\/[^/]+\/test-connect)\/?$/i.test(location.pathname);

  if (isPublicMcpConnectRoute) {
    return (
      <Routes>
        <Route path={'/mcp/:clientShortName/oauth/sign-in'} element={<McpOAuthConnect />} />
        <Route path={'/mcp/:clientShortName/V1/:deploymentName/test-connect'} element={<McpOAuthConnect />} />
      </Routes>
    );
  }

  return (
    <HelpSupportProvider>
      <ApplicationApp />
    </HelpSupportProvider>
  );
};

const ApplicationApp = () => {
  const coreContext = useCoreContext();
  const { canEdit, canView } = useCorePermissions();
  const { getRoute } = useCoreRoute();
  const location = useLocation();
  const commandAccessPending = isClientCommandAccessPending(coreContext);
  const commandAccess = resolveClientCommandAccess({
    canEdit,
    canView,
    profile: coreContext?.Profile,
  });
  const canAccessCommand = canAccessClientCommand({
    canEdit,
    canView,
    profile: coreContext?.Profile,
  });
  const commandRegistry = React.useMemo(
    () => (
      canAccessCommand
        ? resolveClientCommandRegistry({
          registryVersion: coreContext?.ClientCommandRegistryVersion,
          screens: coreContext?.ClientCommandScreens,
          currentEnvironment: coreContext?.ClientCommandCurrentEnvironment,
          displayEnvironment: coreContext?.ClientCommandDisplayEnvironment,
        })
        : null
    ),
    [
      canAccessCommand,
      coreContext?.ClientCommandRegistryVersion,
      coreContext?.ClientCommandScreens,
      coreContext?.ClientCommandCurrentEnvironment,
      coreContext?.ClientCommandDisplayEnvironment,
    ],
  );
  let commandElement = null;
  if (!commandAccessPending) {
    if (!canAccessCommand) {
      commandElement = <CommandAccessDenied />;
    } else if (commandRegistry.status === 'ready') {
      commandElement = (
        <CommandShell
          registry={commandRegistry}
          access={commandAccess}
        />
      );
    } else {
      commandElement = <CommandRegistryError message={commandRegistry.error} />;
    }
  }
  const commandRouteScope = commandRegistry?.status === 'ready'
    ? getClientCommandRouteScope(commandRegistry.screens, location.pathname)
    : null;
  const commandNavigationItems = React.useMemo(
    () => createSelectedClientCommandNavigationItems({
      screens: commandRegistry?.screens,
      clientId: commandRouteScope?.clientId || (
        commandAccess.mode === CLIENT_COMMAND_ACCESS_MODES.OWN_CLIENT
          ? commandAccess.clientId
          : null
      ),
      appId: commandRouteScope?.appId || (
        commandAccess.mode === CLIENT_COMMAND_ACCESS_MODES.OWN_CLIENT
          ? commandAccess.appId
          : coreContext?.Profile?.AppId
      ),
    }),
    [
      commandRegistry?.screens,
      commandRouteScope?.appId,
      commandRouteScope?.clientId,
      commandAccess.appId,
      commandAccess.clientId,
      commandAccess.mode,
      coreContext?.Profile?.AppId,
    ],
  );
  React.useEffect(() => {
    const setCustomNavigationItems = coreContext?.setCustomNavigationItems;
    if (!setCustomNavigationItems) return undefined;

    setCustomNavigationItems(
      CLIENT_COMMAND_NAVIGATION_TARGET_KEY,
      commandNavigationItems,
    );

    return () => {
      setCustomNavigationItems(CLIENT_COMMAND_NAVIGATION_TARGET_KEY, []);
    };
  }, [commandNavigationItems, coreContext?.setCustomNavigationItems]);
  const marketplaceRoute = normalizeRoutePath(getRoute('/marketplace/', 'Marketplace'), '/marketplace');
  const resourceApprovalsRoute = normalizeRoutePath(getRoute('/resource-approvals/manage/', 'Convoke'), '/admin/resource-approvals/manage');
  const executionControlOverviewRoute = normalizeRoutePath(
    getRoute(EXECUTION_CONTROL_OVERVIEW_SCREEN_KEY, EXECUTION_CONTROL_DOMAIN_KEY),
    EXECUTION_CONTROL_OVERVIEW_ROUTE
  );
  const executionControlRoutes = getExecutionControlScreens().map((screen) => ({
    ...screen,
    path: normalizeRoutePath(getRoute(screen.key, EXECUTION_CONTROL_DOMAIN_KEY), screen.routePath),
  }));
  const executionControlPoolProfilesRoute = normalizeRoutePath(
    getRoute('/execution-control/azure-batch/pool-profiles/', EXECUTION_CONTROL_DOMAIN_KEY),
    EXECUTION_CONTROL_POOL_PROFILES_ROUTE
  );
  const batchManagementLegacyRoute = normalizeRoutePath(
    getRoute(BATCH_MANAGEMENT_LEGACY_SCREEN_KEY, 'Convoke'),
    BATCH_MANAGEMENT_LEGACY_ROUTE
  );
  const entityObjectRoutes = buildConvokeEntityObjectRoutes(getRoute);

  // Reserved /login /logout /admin/* 
  return (
    <>
      <SparkApp
        globalLoadingScreen={renderGlobalLoadingScreen}
        staticContentRoutes={[
          '/',
          '/marketplace',
          '/marketplace/:path',
          '/Marketplace',
          '/Marketplace/:path',
          '/command',
          '/command/*',
          EXECUTION_CONTROL_ROOT_ROUTE,
          '/execution-control/:group',
          '/execution-control/:group/:screen',
          BATCH_MANAGEMENT_LEGACY_ROUTE,
          '/mcp/:clientShortName/oauth/sign-in',
          '/mcp/:clientShortName/V1/:deploymentName/test-connect',
        ]}
        addonRoutes={[
        <Route key={"HardLogout"} path={'/logout'} element={<HardLogout />} />,
        <Route key="ClientCommand" path="/command/*" element={commandElement} />,
        <Route key={"McpOAuthConnect"} path={'/mcp/:clientShortName/oauth/sign-in'} element={<McpOAuthConnect />} />,
        <Route key={"McpDeploymentConnect"} path={'/mcp/:clientShortName/V1/:deploymentName/test-connect'} element={<McpOAuthConnect />} />,
        <Route key={"Resource"} element={<DomainRoute component={ResourceDomain} domainName={'Resource'} />} >
          <Route key={"ResourceSchedules"} caseSensitive path={RESOURCES_SCHEDULES_PATH} element={<ResourceWorkspaceRoute />} />
          <Route key={"ResourceTradingPartners"} caseSensitive path={RESOURCES_TRADING_PARTNERS_PATH} element={<EdiTradingPartnerConnectivityManage />} />
          <Route key={"ResourcePartnerGroups"} caseSensitive path={RESOURCES_PARTNER_GROUPS_PATH} element={<EdiTradingPartnerGroupsManage />} />
          <Route key={"ResourceWorkspaceList"} caseSensitive path={RESOURCES_BASE_PATH} element={<ResourceWorkspaceRoute />} />
          <Route key={"ResourceWorkspaceGroup"} caseSensitive path={`${RESOURCE_GROUP_BASE_PATH}/:resourceGroupKey`} element={<ResourceWorkspaceRoute />} />
          <Route key={"ResourceConfigure"} caseSensitive path={`${RESOURCE_WORKSPACE_BASE_PATH}/:clientResourceId/configure`} element={<ResourceConfigurationRoute />} />
          <Route key={"ResourceConfigureConfiguration"} caseSensitive path={`${RESOURCE_WORKSPACE_BASE_PATH}/:clientResourceId/configurations/:resourceConfigurationId/configure`} element={<ResourceConfigurationRoute />} />
          <Route key={"ResourceWorkspace"} caseSensitive path={`${RESOURCE_WORKSPACE_BASE_PATH}/:clientResourceId`} element={<ResourceWorkspaceRoute />} />
          <Route key={"ResourceWorkspaceTool"} caseSensitive path={`${RESOURCE_WORKSPACE_BASE_PATH}/:clientResourceId/:toolKey/*`} element={<ResourceWorkspaceRoute />} />
        </Route>,
        <Route key={"MarketplaceExact"} caseSensitive path={marketplaceRoute} element={<MarketplaceRoute basePath={marketplaceRoute} />} />,
        <Route key={"MarketplaceProduct"} caseSensitive path={`${marketplaceRoute}/products/:productSlug`} element={<MarketplaceRoute basePath={marketplaceRoute} />} />,
        <Route key={"MarketplaceOption"} caseSensitive path={`${marketplaceRoute}/options/:resourceSlug`} element={<MarketplaceRoute basePath={marketplaceRoute} />} />,
        <Route key={"Marketplace"} caseSensitive path={`${marketplaceRoute}/*`} element={<MarketplaceRoute basePath={marketplaceRoute} />} />,
        <Route key={"Home"} element={<DomainRoute component={PublicDomain} domainName={'Public'} supportNoAPI />} > {/* Domain */}
          <Route exact path={'/'} element={<Home />} /> {/* Screens */}
        </Route>,
        <Route element={<DomainRoute component={ReportsDomain} domainName={'Reports'} />} >
          <Route path={getRoute("/reports/", "Reports")} element={<Reports />} /> {/* Screens */}
          <Route path={getRoute("/reports/", "Reports") + '/:id'} element={<ReportCard />} /> {/* Screens */}
        </Route>,
        <Route key={"Convoke1"} element={<DomainRoute component={ConvokeDomain} domainName={'Convoke'} />} >
          <Route path={getRoute("/convoke/sosearch/", "Convoke") + "/:soId"} element={<SearchSourceObject />} /> {/* Screens */}
        </Route>,
        <Route key={"Convoke2"} element={<DomainRoute component={ConvokeDomain} domainName={'Convoke'} />} >
         <Route path={getRoute("/convoke/eosearch/", "Convoke") + "/:eoId"} element={<SearchEntityObject />} /> {/* Screens */}
        </Route>,
        <Route key={"Convoke2b"} element={<DomainRoute component={ConvokeDomain} domainName={'Convoke'} />} >
         <Route path={getRoute("/convoke/whsearch/", "Convoke") + "/:wId"} element={<SearchWarehouse />} /> {/* Screens */}
        </Route>,
        <Route key={"Convoke2c"} element={<DomainRoute component={ConvokeDomain} domainName={'Convoke'} />} >
         <Route path={getRoute("/convoke/warehouseviewsearch/", "Convoke") + "/:warehouseViewId"} element={<SearchWarehouseView />} /> {/* Screens */}
        </Route>,
        <Route key={"Convoke3"} element={<DomainRoute component={ConvokeDomain} domainName={'Convoke'} />} >
          <Route path={getRoute("/convoke/eofieldmapping/", "Convoke") + "/:eoId"} element={<EntityObjectFieldMapping />} /> {/* Screens */}
        </Route>,
        <Route key={"Convoke4"} element={<DomainRoute component={ConvokeDomain} domainName={'Convoke'} />} >
          <Route path={getRoute("/permissionset/manage/", "Convoke") + "/:id"} element={<PermissionSetManage />} /> {/* Screens */}
        </Route>,
        <Route key={"Convoke5"} element={<DomainRoute component={ConvokeDomain} domainName={'Convoke'} />} >
          <Route path={getRoute("/sourceobject/manage/", "Convoke") + "/:id"} element={<SourceColumnSetManage />} /> {/* Screens */}
        </Route>,
        entityObjectRoutes.manage ? (
          <Route key={"Convoke6"} element={<DomainRoute component={ConvokeDomain} domainName={'Convoke'} />} >
            <Route path={`${entityObjectRoutes.manage}/:eoId`} element={<EntityFieldMapDesignerManage />} /> {/* Screens */}
          </Route>
        ) : null,
        entityObjectRoutes.modify ? (
          <Route key={"Convoke6b"} element={<DomainRoute component={ConvokeDomain} domainName={'Convoke'} />} >
            <Route path={`${entityObjectRoutes.modify}/:id`} element={<EntityObjectModify />} /> {/* Screens */}
          </Route>
        ) : null,
        <Route
          key={"ExecutionControlRootAlias"}
          path={EXECUTION_CONTROL_ROOT_ROUTE}
          element={<Navigate to={executionControlOverviewRoute} replace />}
        />,
        <Route
          key={"ExecutionControlPoolStatusChecksAlias"}
          path={EXECUTION_CONTROL_POOL_STATUS_CHECKS_LEGACY_ROUTE}
          element={<Navigate to={executionControlPoolProfilesRoute} replace />}
        />,
        <Route key={"ExecutionControl"} element={<DomainRoute component={ExecutionControlDomain} domainName={'ExecutionControl'} />} >
          {executionControlRoutes.map((screen) => (
            <Route
              key={screen.key}
              path={screen.path}
              element={<ExecutionControlCenter screenKey={screen.key} activeTab={screen.tabKey} />}
            />
          ))}
        </Route>,
        <Route key={"BatchManagementLegacy"} element={<DomainRoute component={ConvokeDomain} domainName={'Convoke'} />} >
          <Route
            path={batchManagementLegacyRoute}
            element={(
              <ExecutionControlCenter
                screenKey={EXECUTION_CONTROL_OVERVIEW_SCREEN_KEY}
                activeTab="overview"
              />
            )}
          />
        </Route>,
        <Route key={"Convoke8"} element={<DomainRoute component={ConvokeDomain} domainName={'Convoke'} />} >
          <Route path={getRoute("/billing/manage/", "Convoke")} element={<BillingManagement />} />
        </Route>,
        <Route key={"Convoke8b"} element={<DomainRoute component={ConvokeDomain} domainName={'Convoke'} />} >
          <Route path={resourceApprovalsRoute} element={<ResourceApprovalsManage />} />
        </Route>,
        <Route key={"Convoke9"} element={<DomainRoute component={ConvokeDomain} domainName={'Convoke'} />} >
          <Route path={`${getRoute("/migration/manage/", "Convoke") ?? "/DataManagement/Migration/Manage"}/:migrationProjectId`} element={<MigrationProjectManage />} />
        </Route>,
        <Route key={"Convoke9b"} element={<DomainRoute component={ConvokeDomain} domainName={'Convoke'} />} >
          <Route path={`${getRoute("/migration/draft/", "Convoke") ?? "/DataManagement/Migration/Draft"}/:migrationProjectId`} element={<MigrationMappingDraftManage />} />
        </Route>,
        <Route key={"Convoke10"} element={<DomainRoute component={ConvokeDomain} domainName={'Convoke'} />} >
          <Route path={`${getRoute("/edi/diagnostics/", "Convoke") ?? "/edi/diagnostics"}/:sourceObjectId`} element={<EDIDiagnosticsManage />} />
        </Route>,
        <Route element={<DomainRoute component={DataDomain} domainName={'Convoke'} />} >
          <Route path={`${getRoute("/model-workspaces/")?.RoutePath ?? '/model-workspaces'}`} element={<DataModelWorkspaceSelect />} />
          <Route path={`${getRoute("/model-workspaces/")?.RoutePath ?? '/model-workspaces'}/:workspaceId`} element={<DataModelDesigner />} />
          <Route path={`${getRoute("/model-workspaces/")?.RoutePath ?? '/model-workspaces'}/:workspaceId/models/new`} element={<DataModelDesigner createMode />} />
          <Route path={`${getRoute("/model-workspaces/")?.RoutePath ?? '/model-workspaces'}/:workspaceId/models/:dataModelId`} element={<DataModelDesigner />} />
        </Route>,
        <Route element={<DomainRoute component={DataDomain} domainName={'Dynamic'} />} >
          <Route path={`${getRoute("/dynamic/datamodelworkspace/")?.RoutePath ?? '/dynamic/datamodelworkspace'}`} element={<DataModelWorkspaceSelect />} />
          <Route path={`${getRoute("/dynamic/datamodelworkspace/")?.RoutePath ?? '/dynamic/datamodelworkspace'}/:workspaceId`} element={<DataModelDesigner />} />
          <Route path={`${getRoute("/dynamic/datamodelworkspace/")?.RoutePath ?? '/dynamic/datamodelworkspace'}/:workspaceId/models/new`} element={<DataModelDesigner createMode />} />
          <Route path={`${getRoute("/dynamic/datamodelworkspace/")?.RoutePath ?? '/dynamic/datamodelworkspace'}/:workspaceId/models/:dataModelId`} element={<DataModelDesigner />} />
        </Route>,
        <Route element={<DomainRoute domainName={'Dashboard'} component={DashboardDomain} />} >
          <Route path={`${getRoute("/dashboard/model/")?.RoutePath ?? '/dashboard/model'}/:dashboardId`} element={<DataModelDesigner dashboardMode />} />
          <Route path={`${getRoute("/dashboard/")?.RoutePath ?? '/dashboard'}`} element={<DashboardSelect />} />
          <Route path={`${getRoute("/dashboard/")?.RoutePath ?? '/dashboard'}/:dashboardId`} element={<Dashboard />} />
          <Route path={`${getRoute("/dashboard/")?.RoutePath ?? '/dashboard'}/:dashboardId/page/:pageId`} element={<Dashboard />} />
         </Route>,
          <Route element={<DomainRoute domainName={'Dashboard'} component={DashboardDomain} />} >
          <Route path={`${getRoute("/dashboard/engineConsole")?.RoutePath ?? '/dashboard/engineConsole'}`} element={<EngineConsoleScreen />} />
         </Route>,
        <Route key={"XChat"} element={<DomainRoute component={XChatDomain} domainName={'XChat'} />} >
          <Route path={getRoute("/multiverx/chat/", "XChat")} element={<XChat />} /> {/* Screens */}
        </Route>,
        <Route
          key={"Status"}
          path={getRoute('/status/', 'SystemStatus') ?? '/status'}
          element={<DomainRoute component={SystemStatusScreen} domainName={'SystemStatus'} />}
        />,
        <Route key={"MarketplaceLegacyExact"} caseSensitive path="/Marketplace" element={<MarketplaceRoute basePath={marketplaceRoute} legacy />} />,
        <Route key={"MarketplaceLegacy"} caseSensitive path="/Marketplace/*" element={<MarketplaceRoute basePath={marketplaceRoute} legacy />} />
        ]} />
      <MarketplaceCheckoutPanelHost />
      <AIPanelCommandHost />
    </>
  )
}

export default App;
